import React from 'react';
import { FileText, Scale, AlertTriangle, Users, Gamepad2, Shield } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';

const TermsPage = () => {
  return (
    <>
      <SEO 
        pageType="page"
        title="Terms of Service - GameHub"
        description="Read our terms of service and user agreement for using GameHub's gaming platform."
        keywords="terms of service, user agreement, gaming terms, website terms"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-4">
              <Scale className="w-8 h-8 text-blue-600 mr-3" />
              <h1 className="text-5xl font-bold text-gray-900">
                Terms of <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Service</span>
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Please read these terms carefully before using GameHub. By using our services, you agree to these terms.
            </p>
            <p className="text-sm text-gray-500 mt-4">Last updated: June 14, 2025</p>
          </div>

          <div className="space-y-8">
            {/* Acceptance of Terms */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <FileText className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Acceptance of Terms</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p>
                  By accessing and using GameHub, you accept and agree to be bound by the terms and provision of this agreement. 
                  If you do not agree to abide by these terms, please do not use this service.
                </p>
                <p>
                  These terms apply to all visitors, users, and others who access or use the service, 
                  including any content, functionality, and services offered on or through GameHub.
                </p>
              </div>
            </Card>

            {/* Use of Service */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Gamepad2 className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Use of Service</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Permitted Use</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>Access and play games for personal, non-commercial use</li>
                    <li>Create an account to save game progress and preferences</li>
                    <li>Share games with friends through provided sharing features</li>
                    <li>Provide feedback and suggestions for improvement</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Prohibited Use</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>Violate any applicable local, state, national, or international law</li>
                    <li>Transmit or distribute viruses, malware, or other harmful code</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Use automated scripts or bots to access the service</li>
                    <li>Copy, modify, or distribute our games without permission</li>
                    <li>Engage in any form of harassment or abusive behavior</li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* User Accounts */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Users className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">User Accounts</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p>
                  When you create an account with us, you must provide information that is accurate, 
                  complete, and current at all times.
                </p>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Account Responsibilities</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>Maintain the security of your password and account</li>
                    <li>Notify us immediately of any unauthorized use</li>
                    <li>Accept responsibility for all activities under your account</li>
                    <li>Provide accurate and truthful information</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600">
                  We reserve the right to refuse service, terminate accounts, or cancel orders 
                  at our sole discretion.
                </p>
              </div>
            </Card>

            {/* Intellectual Property */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Shield className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Intellectual Property Rights</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p>
                  The service and its original content, features, and functionality are and will remain 
                  the exclusive property of GameHub and its licensors.
                </p>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Our Rights</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>All games, graphics, and content are protected by copyright</li>
                    <li>GameHub trademarks and logos are our exclusive property</li>
                    <li>Website design and functionality are protected intellectual property</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">User Content</h3>
                  <p className="text-gray-600">
                    By submitting content (reviews, comments, feedback), you grant us a non-exclusive, 
                    worldwide, royalty-free license to use, modify, and display such content.
                  </p>
                </div>
              </div>
            </Card>

            {/* Privacy Policy */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Shield className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Privacy Policy</h2>
              </div>
              <div className="text-gray-700">
                <p>
                  Your privacy is important to us. Please review our Privacy Policy, which also governs 
                  your use of the service, to understand our practices.
                </p>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-blue-800">
                    <strong>Note:</strong> Our Privacy Policy is incorporated into these Terms of Service by reference.
                  </p>
                </div>
              </div>
            </Card>

            {/* Disclaimers */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <AlertTriangle className="w-6 h-6 text-orange-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Disclaimers</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <p className="text-orange-800 font-semibold mb-2">IMPORTANT DISCLAIMER</p>
                  <p className="text-orange-700 text-sm">
                    The information on this website is provided on an "as is" basis. To the fullest extent 
                    permitted by law, GameHub excludes all representations, warranties, conditions, and terms.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Service Availability</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>We do not guarantee uninterrupted or error-free service</li>
                    <li>Games may be temporarily unavailable for maintenance</li>
                    <li>We reserve the right to modify or discontinue services</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Third-Party Content</h3>
                  <p className="text-gray-600">
                    Some games may be provided by third parties. We are not responsible for 
                    third-party content, services, or websites.
                  </p>
                </div>
              </div>
            </Card>

            {/* Limitation of Liability */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Scale className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Limitation of Liability</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p>
                  In no event shall GameHub, its directors, employees, partners, agents, suppliers, 
                  or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800 font-semibold mb-2">This includes damages for:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm">
                    <li>Loss of profits, data, use, goodwill, or other intangible losses</li>
                    <li>Unauthorized access to or alteration of your transmissions or data</li>
                    <li>Statements or conduct of any third party on the service</li>
                    <li>Any other matter relating to the service</li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* Termination */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Termination</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p>
                  We may terminate or suspend your account and bar access to the service immediately, 
                  without prior notice or liability, under our sole discretion, for any reason whatsoever.
                </p>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Grounds for Termination</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>Breach of these Terms of Service</li>
                    <li>Violation of applicable laws or regulations</li>
                    <li>Fraudulent, abusive, or illegal activity</li>
                    <li>Extended period of inactivity</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600">
                  Upon termination, your right to use the service will cease immediately.
                </p>
              </div>
            </Card>

            {/* Changes to Terms */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
              <div className="text-gray-700">
                <p>
                  We reserve the right, at our sole discretion, to modify or replace these terms at any time. 
                  If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect.
                </p>
                <p className="mt-4">
                  What constitutes a material change will be determined at our sole discretion. 
                  By continuing to access or use our service after any revisions become effective, 
                  you agree to be bound by the revised terms.
                </p>
              </div>
            </Card>

            {/* Contact Information */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <div className="text-gray-700">
                <p className="mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p><strong>Email:</strong> legal@gamehub.com</p>
                  <p><strong>Response Time:</strong> We will respond within 7 business days</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsPage;
